import React, { useState } from "react";
import Marquee from "../components/brand/Marquee";
import Nav from "../components/brand/Nav";
import Footer from "../components/brand/Footer";
import { useScrollReveal } from "../lib/useScrollReveal";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const pageRef = useScrollReveal();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll holler back.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send. Try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={pageRef} data-testid="page-contact" className="bg-bone text-navy">
      <Marquee />
      <Nav />

      {/* HERO */}
      <section className="bg-bone text-navy pt-20 md:pt-28 pb-12 md:pb-16">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12">
          <p className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-red mb-6">
            ◢◢ SAY HELLO
          </p>
          <h1
            data-testid="contact-headline"
            className="font-display uppercase leading-[0.82] tracking-tighter text-[14vw] md:text-[11vw] lg:text-[10vw]"
          >
            DROP US A <span className="text-red">LINE.</span>
          </h1>
        </div>
      </section>

      {/* FORM + SIDE PANEL */}
      <section className="bg-bone text-navy pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 grid grid-cols-12 gap-8 md:gap-12">
          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="col-span-12 md:col-span-7"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              <div>
                <label className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-xs block mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name-input"
                  className="w-full bg-transparent border-4 border-navy rounded-none px-4 py-4 font-mono text-base text-navy placeholder:text-navy/40 brut-focus"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-xs block mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  className="w-full bg-transparent border-4 border-navy rounded-none px-4 py-4 font-mono text-base text-navy placeholder:text-navy/40 brut-focus"
                  placeholder="you@domain.com"
                />
              </div>
            </div>
            <div className="mt-5 md:mt-6">
              <label className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-xs block mb-2">
                Message
              </label>
              <textarea
                required
                rows={7}
                value={form.message}
                onChange={update("message")}
                data-testid="contact-message-input"
                className="w-full bg-transparent border-4 border-navy rounded-none px-4 py-4 font-mono text-base text-navy placeholder:text-navy/40 brut-focus resize-none"
                placeholder="Tell us what's on your mind."
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-7 bg-red text-bone hover:bg-navy hover:text-bone transition-colors font-display uppercase tracking-wider text-xl md:text-2xl px-8 py-4 border-4 border-navy disabled:opacity-60 inline-flex items-center gap-3"
            >
              {loading ? "Sending…" : "Send Message"} <ArrowRight size={22} />
            </button>
          </form>

          {/* Side panel */}
          <aside className="col-span-12 md:col-span-5 md:pl-8 md:border-l-4 md:border-navy">
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-red mb-4">
              ◢ HQ
            </p>
            <p className="font-mono text-base leading-relaxed">
              1850 POLK ST
              <br />
              SAN FRANCISCO · CA
              <br />
              UNITED STATES
            </p>

            <div className="mt-10">
              <p className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-red mb-4">
                ◢ DIRECT LINES
              </p>
              <ul className="font-mono text-base space-y-2">
                <li>
                  <a
                    href="mailto:hello@frdmcoffee.com"
                    data-testid="contact-email-hello"
                    className="hover:text-red"
                  >
                    hello@frdmcoffee.com
                  </a>
                  <span className="block text-navy/60 text-xs uppercase tracking-widest mt-1">
                    general
                  </span>
                </li>
                <li className="mt-4">
                  <a
                    href="mailto:wholesale@frdmcoffee.com"
                    data-testid="contact-email-wholesale"
                    className="hover:text-red"
                  >
                    wholesale@frdmcoffee.com
                  </a>
                  <span className="block text-navy/60 text-xs uppercase tracking-widest mt-1">
                    cafés & retail
                  </span>
                </li>
                <li className="mt-4">
                  <a
                    href="mailto:press@frdmcoffee.com"
                    data-testid="contact-email-press"
                    className="hover:text-red"
                  >
                    press@frdmcoffee.com
                  </a>
                  <span className="block text-navy/60 text-xs uppercase tracking-widest mt-1">
                    press
                  </span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* WHOLESALE TEASER */}
      <section className="bg-navy text-bone py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red mb-3">
              § WHOLESALE
            </p>
            <h2 className="font-display uppercase leading-none tracking-tight text-4xl md:text-6xl">
              Pour FRDM in your <span className="text-red">spot.</span>
            </h2>
            <p className="font-body text-base md:text-lg max-w-md mt-4 text-bone/80">
              Cafés, retailers, hotels — we ship roasted-to-order beans.
            </p>
          </div>
          <a
            href="mailto:wholesale@frdmcoffee.com"
            data-testid="wholesale-cta"
            className="inline-flex items-center gap-3 bg-bone text-navy hover:bg-red hover:text-bone transition-colors font-display uppercase tracking-wider text-xl md:text-2xl px-7 py-4 border-4 border-bone"
          >
            Wholesale <ArrowRight size={22} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
